import { Button } from '@/app/common/components/button/Button'
import PageLoader from '@/app/common/components/PageLoader'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { currencyFormatter } from '@/lib/utils'
import {
  AwardedStudentsPreview,
  FundAwardedStudentsResponse,
} from '@/queries/program_purses/schemas.ts'
import { useAwardedStudentsPreview } from '@/queries/program_purses/useAwardedStudentsPreview.ts'
import { useDownloadAwardedStudentsReport } from '@/queries/program_purses/useDownloadAwardedStudentsReport.ts'
import { useFundAwardedStudents } from '@/queries/program_purses/useFundAwardedStudents.ts'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import {
  AlertCircleIcon,
  AlertTriangle,
  Award,
  CheckCircle2,
} from 'lucide-react'
import { useState } from 'react'
import { getFundAwardedStudentsErrorMessage } from './fund-awarded-students-utils'

/** Accepts both the purse list item and the purse detail resource. */
export type FundablePurse = {
  id: number
  name: string
  balance_available: number
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  purse: FundablePurse
  programId: number
}

export function FundAwardedStudentsDialog({
  open,
  onOpenChange,
  purse,
  programId,
}: Props) {
  const { toast } = useToast()
  const [result, setResult] = useState<FundAwardedStudentsResponse | null>(null)

  const previewQuery = useAwardedStudentsPreview({
    programId,
    purseId: purse.id,
    enabled: open && result === null,
  })

  const downloadMutation = useDownloadAwardedStudentsReport({
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `awarded-students-${programId}-${purse.id}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast({
        title: 'Award report downloaded',
        variant: 'default',
      })
    },
    onError: (error) => {
      toast({
        title: 'Failed to download award report',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const fundMutation = useFundAwardedStudents({
    onSuccess: (data) => {
      setResult(data)

      toast({
        variant: 'default',
        title:
          data.student_purses.length > 0
            ? 'Students Funded'
            : 'Nothing to Fund',
        description:
          data.student_purses.length > 0
            ? `Funded ${data.student_purses.length} awarded student${
                data.student_purses.length === 1 ? '' : 's'
              } for ${currencyFormatter.format(data.total_awarded)} from "${purse.name}".`
            : `No students are waiting in Awarded status on "${purse.name}".`,
      })
    },
    onError: (error: RequestError) => {
      toast({
        variant: 'destructive',
        title: 'Funding Failed',
        description: getFundAwardedStudentsErrorMessage(error, purse.name),
      })
    },
  })

  const handleClose = () => {
    fundMutation.reset()
    downloadMutation.reset()
    setResult(null)
    onOpenChange(false)
  }

  // Read at component level so the footer can render its actions outside PageLoader. Null while
  // the preview is loading or failed, which is exactly when funding must not be offered.
  const preview = previewQuery.isSuccess ? previewQuery.data : null
  const exceedsBalance = preview
    ? preview.total_amount > purse.balance_available
    : false
  const hasAwardedStudents = preview ? preview.total_students > 0 : false

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          if (fundMutation.isPending) return
          handleClose()
          return
        }

        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader className="mb-4">
          <DialogTitle>
            {result ? 'Funding Summary' : `Award Preview - ${purse.name}`}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <FundingSummary purse={purse} result={result} onClose={handleClose} />
        ) : (
          <>
            <div className="space-y-4">
              <PageLoader query={previewQuery}>
                {(loadedPreview) => (
                  <AwardedStudentsSummary
                    preview={loadedPreview}
                    purse={purse}
                    exceedsBalance={exceedsBalance}
                    hasAwardedStudents={hasAwardedStudents}
                  />
                )}
              </PageLoader>

              {fundMutation.isError && (
                <p className="text-sm text-red-600">
                  {getFundAwardedStudentsErrorMessage(
                    fundMutation.error,
                    purse.name
                  )}
                </p>
              )}
            </div>

            {/* Outside PageLoader so the loading and error states keep an explicit way out and
                a way to retry the preview. */}
            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              {preview && (
                <SubmitButton
                  type="button"
                  variant="outline"
                  size="md"
                  status={downloadMutation.status}
                  buttonTxt="Download Award Report"
                  submittingText="Downloading..."
                  className="mr-auto w-auto"
                  forceDisabled={!hasAwardedStudents}
                  onClick={() =>
                    downloadMutation.mutate({
                      programId,
                      purseId: purse.id,
                    })
                  }
                />
              )}
              <div className="flex gap-2 sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={handleClose}
                  disabled={fundMutation.isPending}
                >
                  {preview ? 'Close Preview' : 'Close'}
                </Button>
                {previewQuery.isError && (
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    onClick={() => previewQuery.refetch()}
                    disabled={previewQuery.isFetching}
                  >
                    {previewQuery.isFetching ? 'Retrying...' : 'Retry'}
                  </Button>
                )}
                {preview && (
                  <SubmitButton
                    type="button"
                    size="md"
                    status={fundMutation.status}
                    buttonTxt={
                      <span className="flex items-center gap-2">
                        <Award className="h-4 w-4" aria-hidden="true" />
                        Proceed to Fund
                      </span>
                    }
                    submittingText="Funding..."
                    className="w-auto"
                    forceDisabled={exceedsBalance || !hasAwardedStudents}
                    onClick={() =>
                      fundMutation.mutate({ programId, purseId: purse.id })
                    }
                  />
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function AwardedStudentsSummary({
  preview,
  purse,
  exceedsBalance,
  hasAwardedStudents,
}: {
  preview: AwardedStudentsPreview
  purse: FundablePurse
  exceedsBalance: boolean
  hasAwardedStudents: boolean
}) {
  return (
    <>
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Distribution Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {currencyFormatter.format(preview.total_amount)}
            </div>
            <div className="text-xs text-gray-600">
              Total Amount to be Distributed
            </div>
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">
              {preview.total_students}
            </div>
            <div className="text-xs text-gray-600">Students to be Awarded</div>
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">
              {currencyFormatter.format(purse.balance_available)}
            </div>
            <div className="text-xs text-gray-600">Available in Purse</div>
          </div>
        </div>
      </div>

      {exceedsBalance ? (
        <Callout tone="danger" title="Insufficient Funds">
          The awarded students require{' '}
          {currencyFormatter.format(preview.total_amount)}, which exceeds the
          available balance in this purse (
          {currencyFormatter.format(purse.balance_available)}). Add funds to the
          purse before funding — nothing would be funded otherwise.
        </Callout>
      ) : !hasAwardedStudents ? (
        <Callout tone="warning">
          No students are in <span className="font-medium">Awarded</span> status
          on this purse, so there is nothing to fund.
        </Callout>
      ) : (
        <Callout tone="warning">
          Students are funded with the amounts ServiceNow supplied and cannot be
          edited here. Funding is safe to retry: students already funded for
          this purse are skipped and never funded twice.
        </Callout>
      )}
    </>
  )
}

function Callout({
  tone,
  title,
  children,
}: {
  tone: 'warning' | 'danger'
  title?: string
  children: React.ReactNode
}) {
  const isDanger = tone === 'danger'

  return (
    <div
      className={`flex items-start gap-3 rounded-md border p-3 ${
        isDanger ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'
      }`}
    >
      {isDanger ? (
        <AlertTriangle
          className="mt-0.5 shrink-0 text-red-600"
          aria-hidden="true"
        />
      ) : (
        <AlertCircleIcon
          className="mt-0.5 shrink-0 text-yellow-500"
          aria-hidden="true"
        />
      )}
      <div className={`text-sm ${isDanger ? 'text-red-700' : 'text-gray-700'}`}>
        {title && <p className="font-medium text-red-800 mb-1">{title}</p>}
        {children}
      </div>
    </div>
  )
}

function FundingSummary({
  purse,
  result,
  onClose,
}: {
  purse: FundablePurse
  result: FundAwardedStudentsResponse
  onClose: () => void
}) {
  const fundedStudents = result.student_purses.length

  return (
    <>
      <div className="space-y-4">
        {fundedStudents > 0 ? (
          <div className="flex items-start gap-3">
            <CheckCircle2
              className="mt-0.5 shrink-0 text-green-600"
              aria-hidden="true"
            />
            <p className="text-sm text-gray-700">
              Awarded students on{' '}
              <span className="font-medium">{purse.name}</span> have been funded
              and moved to <span className="font-medium">Funded</span> status.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <AlertCircleIcon
              className="mt-0.5 shrink-0 text-yellow-500"
              aria-hidden="true"
            />
            <p className="text-sm text-gray-700">
              No students were waiting in{' '}
              <span className="font-medium">Awarded</span> status on{' '}
              <span className="font-medium">{purse.name}</span>, so nothing was
              funded.
            </p>
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Students Funded:</span>
            <span className="font-medium">{fundedStudents}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Funded:</span>
            <span className="font-medium">
              {currencyFormatter.format(result.total_awarded)}
            </span>
          </div>
          <div className="border-t pt-2 flex justify-between">
            <span className="font-medium text-gray-900">
              Remaining Purse Balance:
            </span>
            <span className="font-bold text-green-600">
              {currencyFormatter.format(result.balance_available)}
            </span>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="primary" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  )
}
